import { ApiCacheTags, baseApiService } from '@qapline/api';
import { DEFAULT_CONFIG } from '@qapline/config';

import { attachmentSchema, extendedAttachmentSchema, TExtendedAttachment } from '../schemas';
import {
    TAttachmentFileResponseBody,
    TGetAttachmentFileRequestBody,
    TGetAttachmentRequestBody,
    TListAttachmentsRequestBody,
} from '../types';

const attachmentEndpoints = DEFAULT_CONFIG.api.endpoints.attachments;

export const attachmentsApi = baseApiService.injectEndpoints({
    endpoints: (builder) => ({
        listAttachments: builder.query<TExtendedAttachment[], TListAttachmentsRequestBody>({
            query: ({
                commissionId,
                includeCommission,
                includeInvoice,
                includeUploadedBy,
                invoiceId,
                withCommissionsDeliveryNotes,
            }) => {
                const params = new URLSearchParams();

                if (includeUploadedBy) params.append('include[]', 'uploadedBy');
                if (includeCommission) params.append('include[]', 'commission');
                if (includeInvoice) params.append('include[]', 'invoice');
                if (commissionId) params.append('commissionId', commissionId.toString());
                if (invoiceId) params.append('invoiceId', invoiceId.toString());
                if (withCommissionsDeliveryNotes) params.append('withCommissionsDeliveryNotes', 'true');

                return {
                    url: attachmentEndpoints.list,
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response) => extendedAttachmentSchema.array().parse(response),
            providesTags: (_, __, arg) => [
                { type: ApiCacheTags.attachment.list, id: arg.commissionId || arg.invoiceId },
            ],
        }),

        getAttachment: builder.query<TExtendedAttachment, TGetAttachmentRequestBody>({
            query: ({
                attachmentId,
                includeUploadedBy,
                includeCommission,
                includeInvoice,
                includeInvoiceEmailData,
            }) => {
                const params = new URLSearchParams();

                if (includeUploadedBy) params.append('include[]', 'uploadedBy');
                if (includeCommission) params.append('include[]', 'commission');
                if (includeInvoice) params.append('include[]', 'invoice');
                if (includeInvoiceEmailData) params.append('include[]', 'invoiceEmailData');
                return {
                    url: attachmentEndpoints.get.replace(':id', attachmentId.toString()),
                    method: 'GET',
                    params,
                };
            },
            transformResponse: (response) => extendedAttachmentSchema.parse(response),
            providesTags: (_, __, arg) => [{ type: ApiCacheTags.attachment.item, id: JSON.stringify(arg) }],
        }),

        getAttachmentFile: builder.query<TAttachmentFileResponseBody, TGetAttachmentFileRequestBody>({
            query: ({ attachmentId, download }) => {
                return {
                    url: attachmentEndpoints.getFile.replace(':id', attachmentId.toString()),
                    method: 'GET',
                    params: {
                        download,
                    },
                    responseHandler: async (response) => response,
                };
            },
            transformResponse: async (response: Response) => {
                const data = URL.createObjectURL(await response.blob());
                const headers = response.headers;
                return { data, headers };
            },
            providesTags: (_res, _error, args) => [
                ...(args.download ? [] : [{ type: ApiCacheTags.attachment.file, id: args.attachmentId }]),
            ],
        }),
    }),
});

export const {
    useGetAttachmentFileQuery,
    useGetAttachmentQuery,
    useLazyGetAttachmentFileQuery,
    useLazyGetAttachmentQuery,
    useLazyListAttachmentsQuery,
    useListAttachmentsQuery,
} = attachmentsApi;
